import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { TimeRangeConfig, TimeRangeEvents } from '../types';
import { WEB_COMPONENT_GZ_GENERIC_OVERLAY, WEB_COMPONENT_GZ_TIME_RANGE_SLIDER } from '../../../webcomponents.constants';
import moment from 'moment/moment';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { debounce } from '../../../utils/event.utils';
import { GzGenericOverlayWebComponent } from '../../gzGenericOverlay';
import { classMap } from 'lit/directives/class-map.js';
import { AbsoluteTimeRange } from '@nirvana/shared/dtos/types/shared/time';
import { DateRangePickerTranslateStrings } from '../../gzDateRangePicker/types';
import { GzDateRangePickerWebComponent } from '../../gzDateRangePicker';
import { startOfDay } from 'date-fns';

/**
 * @summary This is a range slider component for time ranges.
 * @tag gz-time-range-slider
 * @fires time-range-change - Custom event dispatched when the time range changes
 */
@customElement(WEB_COMPONENT_GZ_TIME_RANGE_SLIDER)
export class GzTimeRangeSliderWebComponent extends LitElement {
  /**
   * The minimum value of the range, for the min slider thumb, as a UNIX timestamp.
   */
  @property({ type: Number, attribute: true, reflect: true })
  valueMinTimestamp: number;

  /**
   * The maximum value of the range, for the min slider thumb, as a UNIX timestamp.
   */
  @property({ type: Number, attribute: true, reflect: true })
  valueMaxTimestamp: number;

  /**
   * The configuration for the time range slider, containing the default min and max values for the slider track, as UNIX timestamps.
   */
  @property({ type: Number, attribute: true, reflect: false })
  timeRangeConfig: TimeRangeConfig;

  /**
   * The format to display the date and time in, above or below the thumbs.
   */
  @property({ type: String, attribute: true, reflect: true })
  dateFormat = 'DD MMM YYYY, HH:mm:ss';

  /** The translation strings for the date range picker. */
  @property({ type: Object, reflect: true })
  translateStrings: DateRangePickerTranslateStrings = {
    confirmButton: 'PLACEHOLDER_APPLY',
    cancelButton: 'PLACEHOLDER_CANCEL',
    fromLabel: 'PLACEHOLDER_FROM',
    toLabel: 'PLACEHOLDER_TO',
  };

  @query('.slider-track')
  private sliderTrack!: HTMLElement;

  @query('.time-range')
  private timeRange!: HTMLElement;

  @query('.min-thumb')
  private minThumb!: HTMLElement;

  @query('.max-thumb')
  private maxThumb!: HTMLElement;

  @query('.min-label')
  private minLabel!: HTMLElement;

  @query('.max-label')
  private maxLabel!: HTMLElement;

  @query('gz-date-range-picker')
  private dateRangePicker!: GzDateRangePickerWebComponent;

  @query(WEB_COMPONENT_GZ_GENERIC_OVERLAY)
  private calendarPickerOverlay!: GzGenericOverlayWebComponent;

  @state()
  private calendarSelected = false;

  @state()
  private invalidRange = false;

  private resizeObserver: ResizeObserver;

  static get styles() {
    return styles;
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (!this.timeRangeConfig) {
      this.timeRangeConfig = { minTimestamp: 0, maxTimestamp: new Date().getTime() };
    }
    if (!this.valueMinTimestamp) {
      this.valueMinTimestamp = this.timeRangeConfig.minTimestamp;
    }
    if (!this.valueMaxTimestamp) {
      this.valueMaxTimestamp = this.timeRangeConfig.maxTimestamp;
    }
  }

  firstUpdated(): void {
    this.resizeObserver = new ResizeObserver(debounce(this.updateRange.bind(this), 100));
    if (this.sliderTrack) {
      this.resizeObserver.observe(this.sliderTrack);
    }
  }

  updated(changedProps: PropertyValues): void {
    if (changedProps.has('valueMinTimestamp') || changedProps.has('valueMaxTimestamp')) {
      this.updateComplete.then(() => this.updateRange());
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.sliderTrack) this.resizeObserver?.unobserve(this.sliderTrack);
  }

  render(): TemplateResult {
    const backgroundClasses = { 'slider-background': true, 'invalid-range': this.invalidRange };
    return html`
      <div class="${classMap(backgroundClasses)}" @click="${this.handleMouseClick}">
        <div class="slider-track">
          <div class="time-range">
            <div class="min-thumb">
              <div class="min-label">${this.getMinLabel()}</div>
            </div>
            <div class="max-thumb">
              <div class="max-label">${this.getMaxLabel()}</div>
            </div>
          </div>
        </div>
      </div>
      <gz-generic-overlay ?overlayID="calendarOverlay" .attachToBody="${false}" .overlayVerticalSpacer="${12}">
        <gz-date-range-picker
          showTimePickers
          displaySecondsInput
          min="${this.timeRangeConfig.minTimestamp}"
          max="${this.timeRangeConfig.maxTimestamp}"
          fromTimestamp="${this.valueMinTimestamp}"
          toTimestamp="${this.valueMaxTimestamp}"
          @selectedRangeConfirmed="${this.handleNewSelectedRange}"
          @selectedRangeCancelled="${this.closeCalendarPickerOverlay}"
          @customHoursChanged="${this.validateRange}"
          @customDateChanged="${this.handleDateChange}"
          .translateStrings="${this.translateStrings}"
        ></gz-date-range-picker>
      </gz-generic-overlay>
    `;
  }

  private updateRange(): void {
    // called on resize or on valueMinTimestamp/valueMaxTimestamp attribute change
    this.validateTimestamps();
    this.computeRangeStyles();
    elementClassListRemove(this.timeRange, 'd-none'); // this will also make the thumbs, relatively positioned to the time range, visible
  }

  private validateTimestamps(): void {
    let error: string | undefined;
    if (this.timeRangeConfig.minTimestamp > this.timeRangeConfig.maxTimestamp) {
      error = 'The timeRangeConfig.maxTimestamp must be greater than timeRangeConfig.minTimestamp.';
    } else if (this.valueMinTimestamp > this.valueMaxTimestamp) {
      error = 'The valueMinTimestamp must be greater than the valueMaxTimestamp.';
    } else if (
      this.valueMinTimestamp < this.timeRangeConfig.minTimestamp ||
      this.valueMaxTimestamp > this.timeRangeConfig.maxTimestamp
    ) {
      error = 'The valueMinTimestamp and valueMaxTimestamp must be within the range of the time range config.';
    }
    if (error) {
      elementClassListAdd(this.timeRange, 'd-none');
      this.invalidRange = true;
      throw new Error(error);
    }
    this.invalidRange = false;
  }

  private computeRangeStyles(): void {
    const trackLength = this.sliderTrack.clientWidth;
    const totalTime = this.timeRangeConfig.maxTimestamp - this.timeRangeConfig.minTimestamp;
    let rangeLeft = 0;
    let rangeRight = trackLength;
    if (totalTime > 0) {
      rangeLeft = ((this.valueMinTimestamp - this.timeRangeConfig.minTimestamp) / totalTime) * trackLength;
      rangeRight = ((this.valueMaxTimestamp - this.timeRangeConfig.minTimestamp) / totalTime) * trackLength;
    }
    const minLabelOffset = (this.minLabel.clientWidth - this.minThumb.offsetWidth) / 2; // offsetWidth includes border
    const maxLabelOffset = (this.maxLabel.clientWidth - this.maxThumb.offsetWidth) / 2;
    const leftThumbRightArea = trackLength - rangeLeft - this.minThumb.offsetWidth; // the area to the right of the left thumb
    const rightThumbLeftArea = rangeRight - this.maxThumb.offsetWidth; // the area to the left of the right thumb

    // set the left and width of the time range. The thumbs are relatively positioned to the time range edges via CSS.
    this.timeRange.style.left = `${rangeLeft}px`;
    this.timeRange.style.width = `${rangeRight - rangeLeft}px`;

    // ensure the labels do not exceed the slider track edges. The labels are relatively positioned to the thumbs' center via CSS.
    this.minLabel.style.left = `${Math.min(
      leftThumbRightArea - minLabelOffset,
      Math.max(0, minLabelOffset - rangeLeft)
    )}px`;
    this.maxLabel.style.right = `${Math.min(
      rightThumbLeftArea - maxLabelOffset,
      Math.max(0, maxLabelOffset - (trackLength - rangeRight))
    )}px`;
  }

  private handleMouseClick(): void {
    if (this.invalidRange) {
      return;
    }
    this.calendarPickerOverlay.openOverlay(this.sliderTrack);

    if (!this.dateRangePicker) {
      return;
    }

    if (this.calendarSelected) {
      this.dateRangePicker.fromTimestamp = this.valueMinTimestamp;
      this.dateRangePicker.toTimestamp = this.valueMaxTimestamp;
      this.dateRangePicker.updateInputsToValue(this.valueMinTimestamp, this.valueMaxTimestamp);
    } else {
      this.dateRangePicker.updateInputsToValue(this.timeRangeConfig.minTimestamp, this.timeRangeConfig.maxTimestamp);
    }
  }

  private handleDateChange(e: CustomEvent): void {
    if (e.detail) {
      this.updateTimeInputs(
        e.detail.timeStampValue,
        e.detail.key,
        this.timeRangeConfig.minTimestamp,
        this.timeRangeConfig.maxTimestamp
      );
    }
    this.validateRange();
  }

  private validateRange(): void {
    if (!this.dateRangePicker) {
      return;
    }

    this.dateRangePicker.validateRange();
  }

  updateTimeInputs(clickedTimeStamp: number | null, key: string, min, max): void {
    const day = clickedTimeStamp;

    if (!day) {
      return;
    }

    const dayDate = startOfDay(day * 1000).getTime();
    const minDate = startOfDay(min * 1000).getTime();
    const isMaxZero = max === 0;
    const maxDate = isMaxZero ? 0 : startOfDay(max * 1000).getTime();

    const dayYear = new Date(dayDate).getFullYear();
    const minYear = new Date(minDate).getFullYear();
    const maxYear = isMaxZero ? 0 : new Date(maxDate).getFullYear();

    function calculateDayOfYear(date: number, year: number): number {
      return Math.floor((date - startOfDay(new Date(year, 0, 1)).getTime()) / (1000 * 60 * 60 * 24));
    }

    const dayOfYear = calculateDayOfYear(dayDate, dayYear);
    const minOfYear = calculateDayOfYear(minDate, minYear);
    const maxOfYear = isMaxZero ? 0 : calculateDayOfYear(maxDate, maxYear);

    const matchesMin = dayYear === minYear && dayOfYear === minOfYear;
    const matchesMax = !isMaxZero && dayYear === maxYear && dayOfYear === maxOfYear;

    if (matchesMin && key === 'fromTimestamp') {
      this.dateRangePicker.updateFromInput(this.timeRangeConfig.minTimestamp);
    }

    if (matchesMax && key === 'toTimestamp') {
      this.dateRangePicker.updateToInput(this.timeRangeConfig.maxTimestamp);
    }
  }

  private handleNewSelectedRange(event: CustomEvent): void {
    const timeRange = event.detail as AbsoluteTimeRange;
    this.valueMinTimestamp = timeRange.fromTimestamp ?? 0;
    this.valueMaxTimestamp = timeRange.toTimestamp ?? 0;
    this.closeCalendarPickerOverlay();
    this.calendarSelected = true;

    this.dispatchEvent(
      new CustomEvent(TimeRangeEvents.TIME_RANGE_CHANGE, {
        detail: { valueMinTimestamp: this.valueMinTimestamp, valueMaxTimestamp: this.valueMaxTimestamp },
      })
    );
  }

  private closeCalendarPickerOverlay(): void {
    this.calendarPickerOverlay.closeOverlay();
  }

  private getMinLabel(): string {
    return moment(this.valueMinTimestamp * 1000).format(this.dateFormat);
  }

  private getMaxLabel(): string {
    return moment(this.valueMaxTimestamp * 1000).format(this.dateFormat);
  }
}
